import * as Sentry from '@sentry/react';
import { Component, type ReactNode } from 'react';
import styled from 'styled-components';

import { Button } from 'app/components/customers/Button';
import { Spinner } from 'app/components/customers/Spinner';

import { MAX_JITTER_MS, scheduleChunkReload } from './chunkReload';

export const FallbackWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
`;

const RELOAD_GRACE_MS = 10_000;

type ChunkErrorBoundaryProps = {
  children: ReactNode;
  flow: string;
  message: string;
  resetKey?: string;
};

type ChunkErrorBoundaryState = { hasError: boolean; isReloading: boolean };

// The page chunk can fail to download (offline, stale deploy); the user must not be left on a blank screen.
export class ChunkErrorBoundary extends Component<ChunkErrorBoundaryProps, ChunkErrorBoundaryState> {
  state: ChunkErrorBoundaryState = { hasError: false, isReloading: false };

  private reloadWatchdog?: ReturnType<typeof setTimeout>;

  static getDerivedStateFromError(): Partial<ChunkErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    // webpack 5 also reports a failed CSS chunk (code CSS_CHUNK_LOAD_FAILED) with name ChunkLoadError
    const isChunkLoadError = error?.name === 'ChunkLoadError';
    Sentry.withScope(scope => {
      scope.setTag('flow', `${this.props.flow}_${isChunkLoadError ? 'chunk' : 'render'}`);
      Sentry.captureException(error);
    });
    if (isChunkLoadError && scheduleChunkReload()) {
      this.setState({ isReloading: true });
      // beforeunload can cancel the scheduled reload; without this the spinner would never end
      this.reloadWatchdog = setTimeout(() => this.setState({ isReloading: false }), MAX_JITTER_MS + RELOAD_GRACE_MS);
    }
  }

  componentDidUpdate(previousProps: ChunkErrorBoundaryProps) {
    if (this.state.hasError && previousProps.resetKey !== this.props.resetKey) {
      clearTimeout(this.reloadWatchdog);
      this.setState({ hasError: false, isReloading: false });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.reloadWatchdog);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }
    if (this.state.isReloading) {
      return (
        <FallbackWrapper>
          <Spinner />
        </FallbackWrapper>
      );
    }
    return (
      <FallbackWrapper role="alert">
        <div>
          <p>{this.props.message}</p>
          <Button onClick={() => window.location.reload()}>Recarregar página</Button>
        </div>
      </FallbackWrapper>
    );
  }
}
